import { watchFields } from "@omnia/shared/fields/watch"
import { CroppingGroupVm } from "../../croppingGroup"
import { IOmniaSettings, IOmniaShell } from "../../omnia"
import { CroppingGroupService } from "../../services/grouping"
import { NutrientFilter } from "../../services/nutrientFilter"
import { setupStocks } from "../../stocks/utils"
import { ICroppingGrouping, IFieldsService, INutrientGroup, INutritionService } from "../../types"
import { getNutrientGroups, LimeEnabledFromYear } from "../../utils"
import { TableDashboardContext } from "./dashboardContext"
import { PlanCroppingVm } from "./vm/planCroppingVm"
import { PlanVm } from "./vm/planVm"
import { isCropSupportedForNutrition } from "@omnia/shared/nutrition"
import { nutrientColors } from "@omnia/shared/nutrition/colors"
import { Component } from "@omnia/shared/shell/component"
import { defineComponent } from "@omnia/shared/utils/angular"
import { canEditGroupRequirements, canResetGroupRequirements, hasAuthorizationsOnAnyInGroup } from "./permissions"

export type CroppingGroup = CroppingGroupVm<PlanCroppingVm>


export interface IPlanFilterItem {
    id: number;
    text: string;
}

const showRequirementsLeftSetting = "ShowRequirementLeft"

export class DashboardCtrl extends Component {

    static create() {

        return defineComponent(DashboardCtrl, "app/nutrition/dashboard/table/dashboard.html", {
            module: "="
        })

    }

    private _fieldsWatcher: Function

    private settings: IOmniaSettings;

    public dashboard: TableDashboardContext;

    public plans: PlanVm[];

    private _allCropping: PlanCroppingVm[];

    public cropping: PlanCroppingVm[];
    public croppingGroups: CroppingGroup[];

    private _croppingCardsView: boolean;

    public columnsCount: number = 2;
    public columns: number[] = [0, 1];

    public _showZones: boolean;

    public selectedPlan: IPlanFilterItem;
    public selectPlansLst: IPlanFilterItem[];

    private _nutrients: NutrientFilter;
    private _nutrientGroup: INutrientGroup;
    public nutrientGroups: INutrientGroup[];

    public planView: boolean;

    static $inject = [
        "$scope",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaNutritionCroppingGroup",
        "OmniaShell",
    ];

    constructor(
        $scope: ng.IScope,
        private readonly svc: INutritionService,
        private readonly fieldSvc: IFieldsService,
        private readonly groupingSvc: CroppingGroupService,
        private readonly shell: IOmniaShell,
        private readonly session = shell.getSession()
    ) {

        super($scope)

        this.settings = this.svc.settings;

    }

    $onInit() {
        this.$scope.$watch("vm.svc.stateVersion", (a, b) => (a !== b) && this.refresh())
        this.refresh().then(() => this.startInitialComponent())
    }

    $onDestroy() {
        this._fieldsWatcher && this._fieldsWatcher()
        this._fieldsWatcher = undefined
    }


    private refresh() {

        return this.svc.refresh()
            .then(() => {

                this.dashboard = new TableDashboardContext(this.svc, this.shell, () => this.refresh())

                this.groupingSvc.refresh()
                this.setupFilters()
                this.setupPlans()

            })
            .then(() => {

                if (!this._fieldsWatcher) {
                    return watchFields(this.fieldSvc, this.$scope, () => this.setupCropping()).then(r => this._fieldsWatcher = r)
                }
                else {
                    this.setupCropping()
                }

            })

    }

    private startInitialComponent() {

        let params = this.shell.getRouteParams() || {};

        let component = params.action;
        let id = params.id;

        switch (component) {

            case "edit-plan":
                let plan = this.plans.find(p => p.plan.NutritionPlanId == id);
                if (plan) {
                    this.dashboard.updatePlan(plan, null);
                }
                break;

        }

    }

    private setupFilters() {

        this._nutrients = new NutrientFilter(this.session, this.settings);

        let groups = getNutrientGroups(this.session.yearId >= LimeEnabledFromYear, "Required");

        this.nutrientGroups = groups.groups;

        let currentGroup = this.nutrientGroups.find(g => g.key == this._nutrients.nutrientGroupKey) || this.nutrientGroups[0];
        this._nutrientGroup = currentGroup;
        this.dashboard.nutrientGroup = currentGroup;

        this.dashboard.showRequirementsLeft = this.settings.get(showRequirementsLeftSetting, true);

    }

    protected setupCropping() {

        let cropping = this.svc.cropping.filter(c => c.currentYear && c.visible)

        cropping = cropping.filter(c => isCropSupportedForNutrition(c))

        this._allCropping = PlanCroppingVm.createFromCropping(this.svc, cropping, this.plans)

        this._croppingCardsView = this.settings.get("croppingCardsView")

        this._showZones = this.settings.get("showZones")
        this.dashboard.showZones = this.showZones

        this.filterCropping()

    }

    protected setupPlans() {

        const stocks = setupStocks(this.svc)

        this.plans = this.svc.getPlans()
            .sort((p1, p2) => p1.Name.localeCompare(p2.Name))
            .map(p => new PlanVm(p, this.svc, stocks));

        this.selectPlansLst = [{ id: 0, text: "All plans" }].concat(this.plans.map(p => {
            return {
                id: p.plan.NutritionPlanId,
                text: p.Name
            }
        }).sort((a, b) => a.text > b.text ? 1 : a.text < b.text ? -1 : 0));
        this.selectedPlan = this.selectPlansLst[0];
    }


    public filterCropping() {

        let cropping = this._allCropping.slice();

        this.cropping = cropping;
        this.dashboard.setupCropping(cropping);

        this.updateCroppingGroups();

    }

    public updateCroppingGroups(grouping?: ICroppingGrouping) {

        const planId = this.selectedPlan ? this.selectedPlan.id : 0;

        const cropping = this.cropping.filter(c => (!planId) || c.containsPlan(planId))

        this.croppingGroups = this.groupingSvc.updateCroppingGroups(cropping, grouping);

        this.dashboard.croppingGroups = this.croppingGroups;

    }


    get ready(): boolean {
        return this.svc.ready;
    }

    get readOnly() {
        const { dashboard } = this
        return dashboard ? (dashboard.edit || dashboard.readonly) : true
    }

    get showRequirementsLeft(): boolean {
        return this.dashboard.showRequirementsLeft;
    }

    set showRequirementsLeft(value: boolean) {
        this.dashboard.showRequirementsLeft = value;
        this.settings.set(showRequirementsLeftSetting, value);
    }

    get nutrientGroup(): INutrientGroup {
        return this._nutrientGroup;
    }

    set nutrientGroup(value: INutrientGroup) {
        this._nutrientGroup = value;
        this._nutrients.nutrientGroup = value ? this._nutrients.nutrientGroups.find(g => g.key == value.key) : null;
    }

    get columnsClass(): any {
        return { 'col-lg-12': true, 'col-xl-6': true };
    };

    get croppingGrouped(): boolean {
        return this.dashboard.grouped;
    }

    get croppingCardsView(): boolean {
        return this._croppingCardsView && this.dashboard.grouped;
    }

    set croppingCardsView(value: boolean) {
        this._croppingCardsView = value;
        this.settings.set("croppingCardsView", value ? true : null);
    }

    get planFilter() {
        return this.svc.planFilter
    }

    set planFilter(value) {
        // Note: filters are applied in the tables/plans/plans.ts component
        // The value is passed by binding
        this.svc.planFilter = value
    }

    get showZones(): boolean {
        return this._showZones;
    }

    set showZones(value: boolean) {
        this._showZones = value;
        if (this.dashboard) {
            this.dashboard.showZones = value;
        }
        this.settings.set("showZones", value ? true : null);
    }

    get planId(): number {
        return this.dashboard.selectedPlan ? this.dashboard.selectedPlan.plan.NutritionPlanId : undefined;
    }

    showCropsUngrouped(): boolean {
        return this.groupingSvc && this.groupingSvc.isUngrouped();
    }

    canChangeSelection(): boolean {
        if (this.dashboard) return this.dashboard.canChangeSelection();
    }

    toggleSelection(c: PlanCroppingVm) {
        if (this.dashboard) this.dashboard.toggleSelection(c);
    }

    get currentGrouping(): ICroppingGrouping {
        return this.groupingSvc.currentGrouping;
    }

    getGroupings(): ICroppingGrouping[] {
        return this.groupingSvc.groupings;
    }

    selectCroppingGrouping(grouping: ICroppingGrouping) {

        this.updateCroppingGroups(grouping);

    }

    getValueColor(c: PlanCroppingVm, key: string) {

        return nutrientColors[key].baseColor;

    }

    getValueCellStyle(c: PlanCroppingVm, key: string) {

        if (this.dashboard) {
            return this.dashboard.getValueCellStyle(c, key);
        }

    }

    getPlansInGridColumn(column: number): PlanVm[] {

        return this.getItemsInGridColumn(this.plans, column);

    }

    getCroppingGroupInGridColumn(column: number): CroppingGroup[] {

        return this.getItemsInGridColumn(this.croppingGroups, column);

    }

    getItemsInGridColumn<T>(items: T[], column: number): T[] {

        var columns = this.columnsCount;

        return items.filter((item, index) => (index % columns) == column);

    }

    canCalculateRequirements(): boolean {
        return this.cropping && hasAuthorizationsOnAnyInGroup(this.cropping)
    }

    canCalculateGroupRequirements(group: CroppingGroup) {
        return hasAuthorizationsOnAnyInGroup(group)
    }

    calculateRequirements(cropping: PlanCroppingVm[]) {

        this.dashboard.calculateRequirements(cropping);

    }

    calculateGroupRequirements(group: CroppingGroup) {

        return this.dashboard.calculateGroupRequirements(group);

    }

    canEditRequirements(): boolean {
        return this.cropping && this.cropping.filter(c => c.hasRequirements).length > 0;
    }

    editRequirements() {

        if (this.croppingGroups.length && !this.cropping.filter(c => c.selected).length) {
            var group = this.croppingGroups.find(g => g.cropping.filter(c => c.hasRequirements).length > 0);
            if (group) {
                var cropping = group.cropping.find(c => c.hasRequirements);
                if (cropping) {
                    cropping.selected = true;
                }
            }
        } else if (this.cropping.filter(c => c.selected).length > 1) {
            var cropping = this.cropping.filter(c => c.selected)[0];
            this.cropping.filter(c => c.selected && c !== cropping).forEach(c => c.selected = false);
        }

        this.dashboard.editRequirements();

    }

    canEditGroupRequirements(group: CroppingGroup) {
        return canEditGroupRequirements(group)
    }

    editGroupRequirements(group: CroppingGroup) {

        if (group) {
            if (!group.cropping.filter(c => c.selected).length) {
                this.cropping.filter(c => c.selected).forEach(c => c.selected = false);
                group.cropping.filter(c => c.hasRequirements)[0].selected = true;
            } else if (group.cropping.filter(c => c.selected).length > 1) {
                var cropping = this.cropping.filter(c => c.selected)[0];
                this.cropping.filter(c => c.selected && c !== cropping).forEach(c => c.selected = false);
            }

            return this.dashboard.editRequirements();
        }

    }

    canCreateNewPlan(): boolean {

        var cropping = this.cropping.filter(c => c.selected);
        if (!cropping.length) cropping = null;

        return this.dashboard.canCreateNewPlan(cropping);

    }

    createNewPlan(cropping: PlanCroppingVm[]) {
        this.dashboard.createNewPlan(cropping).then(r => {
            this.setupPlans();
            this.setupCropping();
        });
    }

    editPlan(planVm: PlanVm, $event: ng.IAngularEvent) {
        this.dashboard.updatePlan(planVm, $event).then(r => {
            this.setupPlans();
            this.setupCropping();
        });
    }

    public selectAllCropping(select: boolean = true) {
        this.cropping.filter(c => !c.isZone).forEach(c => c.selected = select && c.authorized);
    }

    canCreateNewPlanForGroup(group: CroppingGroup): boolean {
        return this.dashboard?.canCreateNewPlanForGroup(group);
    }

    createNewPlanForGroup(group: CroppingGroup) {

        if (this.dashboard) {
            return this.dashboard.createNewPlanForGroup(group);
        }

    }

    canResetRequirements(): boolean {
        if (this.readOnly) return false
        return !!this.cropping.filter(c => c.selected && !!c.requirements && !c.isInPlan).length;
    }

    resetRequirements() {

        var ids = this.cropping.filter(c => c.selected && !!c.requirements && !c.isInPlan).map(c => c.croppingId);

        this.dashboard.resetRequirements(ids).then(r => {
            if (r) {
                this.setupPlans();
                this.setupCropping();
            }
        });

    }

    canResetGroupRequirements(group: CroppingGroup) {
        return canResetGroupRequirements(group)
    }

    resetGroupRequirements(group: CroppingGroup) {

        var selection = group.cropping.filter(c => c.selected);
        if (!selection.length) {
            selection = group.cropping;
        }

        var ids = selection.filter(c => !!c.requirements && !c.isInPlan && !c.isZone).map(c => c.croppingId);

        this.dashboard.resetRequirements(ids).then(r => {
            if (r) {
                this.dashboard.refresh();
            }
        });

    }

    selectPlan(item: IPlanFilterItem) {

        this.selectedPlan = item;
        this.dashboard.selectedPlan = item || item.id === 0 ? this.plans.find(p => p.plan.NutritionPlanId === item.id) : null;
        this.updateCroppingGroups();

    }

    selectNutrientGroup(group: INutrientGroup) {

        this.nutrientGroup = group;
        this.dashboard.nutrientGroup = group;
        this.updateCroppingGroups();

    }
}
